// ExplanationPanel component - shows algorithm details and properties
import type { SortAlgorithm } from '@/core/types';
import { Colors } from '@/styles/colors';
import { formatCategoryLabel } from './copy';

interface ExplanationPanelProps {
  algorithm: SortAlgorithm;
}

const complexityExamples = {
  bubble: 'Data sudah urut: hanya n-1 perbandingan dan tidak ada pertukaran.',
  selection: 'Data sudah urut pun tetap dipindai penuh untuk mencari nilai minimum.',
  insertion: 'Data hampir urut biasanya lebih cepat karena sedikit nilai yang perlu digeser.',
  merge: 'Performa stabil di banyak kondisi, tetapi butuh ruang tambahan untuk proses merge.',
  quick: 'Sangat cepat jika partisi seimbang, tetapi bisa berat jika pivot buruk.',
  heap: 'Konsisten untuk data besar, tetapi urutan nilai yang sama tidak selalu dipertahankan.',
  shell: 'Mempercepat Insertion Sort dengan membandingkan data berjarak tertentu lebih dulu.',
};

export function ExplanationPanel({ algorithm }: ExplanationPanelProps) {
  const getCategoryDescription = (category: string): string => {
    switch (category) {
      case 'comparison':
        return 'Algoritma ini mengurutkan data dengan membandingkan dua nilai, lalu memutuskan mana yang harus lebih dulu.';
      case 'non-comparison':
        return 'Algoritma ini memakai pola atau sifat data tertentu, sehingga tidak selalu perlu membandingkan nilai satu per satu.';
      case 'hybrid':
        return 'Algoritma ini menggabungkan beberapa teknik agar lebih efisien pada kondisi data yang berbeda.';
      default:
        return '';
    }
  };

  const getPropertyDescription = (name: string, value: boolean): string => {
    switch (name) {
      case 'stable':
        return value ? 'Nilai yang sama tetap mempertahankan urutan awalnya.' : 'Nilai yang sama bisa berubah urutan setelah proses sorting.';
      case 'inPlace':
        return value ? 'Mengurutkan langsung di array utama dengan memori tambahan kecil.' : 'Membutuhkan memori tambahan untuk membantu proses sorting.';
      case 'adaptive':
        return value ? 'Bisa lebih cepat saat data awalnya sudah hampir urut.' : 'Kecepatan tidak banyak terbantu oleh data yang sudah hampir urut.';
      default:
        return '';
    }
  };

  const getExample = (): string => {
    const key = algorithm.name.toLowerCase().split(' ')[0] as keyof typeof complexityExamples;
    return complexityExamples[key] ?? 'Coba beberapa jenis data untuk melihat perbedaannya.';
  };

  return (
    <div className="explanation-panel" style={{ backgroundColor: Colors.light.surface, border: `1px solid ${Colors.light.border}`, borderRadius: '8px', padding: '20px', flex: '1', minWidth: 0 }}>
      <h2 style={{ margin: '0 0 20px 0', color: Colors.light.primary }}>Penjelasan sederhana</h2>

      <div style={{ marginBottom: '20px' }}>
        <h3 style={{ margin: '0 0 8px 0', color: Colors.light.text }}>{algorithm.name}</h3>
        <div style={{ margin: '0 0 8px 0', color: Colors.light.textSecondary, fontSize: '12px' }}>
          {formatCategoryLabel(algorithm.category)}
        </div>
        <p style={{ margin: 0, lineHeight: '1.6', color: Colors.light.textSecondary, fontSize: '14px' }}>
          {getCategoryDescription(algorithm.category)}
        </p>
      </div>

      <div style={{ marginBottom: '20px' }}>
        <h4 style={{ margin: '0 0 10px 0', fontSize: '14px', color: Colors.light.text }}>Sifat penting</h4>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
          {[
            ['Stabil', algorithm.stable, getPropertyDescription('stable', algorithm.stable)],
            ['In-place', algorithm.inPlace, getPropertyDescription('inPlace', algorithm.inPlace)],
            ['Adaptif', algorithm.adaptive, getPropertyDescription('adaptive', algorithm.adaptive)],
          ].map(([label, value, description]) => (
            <div key={String(label)} className="property-row" style={{ display: 'grid', gridTemplateColumns: '82px 58px 1fr', gap: '10px', alignItems: 'center' }}>
              <div style={{ fontSize: '12px', color: Colors.light.textSecondary }}>{label}:</div>
              <div
                style={{
                  padding: '2px 8px',
                  borderRadius: '4px',
                  backgroundColor: value ? Colors.light.success + '20' : Colors.light.error + '20',
                  color: value ? Colors.light.success : Colors.light.error,
                  fontSize: '11px',
                  textAlign: 'center',
                }}
              >
                {value ? 'Ya' : 'Tidak'}
              </div>
              <div style={{ fontSize: '11px', color: Colors.light.textSecondary }}>{description}</div>
            </div>
          ))}
        </div>
      </div>

      <div style={{ marginBottom: '20px' }}>
        <h4 style={{ margin: '0 0 10px 0', fontSize: '14px', color: Colors.light.text }}>Kompleksitas waktu</h4>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(120px, 1fr))', gap: '10px' }}>
          <div>
            <div style={{ fontSize: '11px', fontWeight: 'bold', color: Colors.light.textSecondary }}>Terbaik</div>
            <div style={{ fontSize: '12px', color: Colors.light.text }}>{algorithm.best}</div>
          </div>
          <div>
            <div style={{ fontSize: '11px', fontWeight: 'bold', color: Colors.light.textSecondary }}>Rata-rata</div>
            <div style={{ fontSize: '12px', color: Colors.light.text }}>{algorithm.average}</div>
          </div>
          <div>
            <div style={{ fontSize: '11px', fontWeight: 'bold', color: Colors.light.textSecondary }}>Terburuk</div>
            <div style={{ fontSize: '12px', color: Colors.light.text }}>{algorithm.worst}</div>
          </div>
        </div>
        <div style={{ marginTop: '10px', fontSize: '12px', color: Colors.light.textSecondary, lineHeight: 1.6 }}>
          {getExample()}
        </div>
      </div>

      <div style={{ marginBottom: '20px' }}>
        <h4 style={{ margin: '0 0 10px 0', fontSize: '14px', color: Colors.light.text }}>Kompleksitas ruang</h4>
        <div style={{ padding: '8px', backgroundColor: Colors.light.background, borderRadius: '4px', fontSize: '13px', color: Colors.light.text }}>
          {algorithm.space}
        </div>
      </div>

      <div style={{ marginTop: '20px' }}>
        <h4 style={{ margin: '0 0 10px 0', fontSize: '14px', color: Colors.light.text }}>Inti yang perlu diingat</h4>
        <div style={{ padding: '10px', backgroundColor: Colors.light.background, borderRadius: '4px', fontSize: '13px', color: Colors.light.textSecondary, lineHeight: '1.6' }}>
          Tidak ada algoritma sorting yang selalu paling baik. Setiap algoritma punya kelebihan dan batasan. Gunakan pilihan jenis data untuk melihat perbedaannya secara visual.
        </div>
      </div>
    </div>
  );
}
